import Password from './password'
import moment from './moment'

const REGEX_EMAIL = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
const REGEX_NUMBER = /\+(9[976]\d|8[987530]\d|6[987]\d|5[90]\d|42\d|3[875]\d|2[98654321]\d|9[8543210]|8[6421]|6[6543210]|5[87654321]|4[987654310]|3[9643210]|2[70]|7|1)\d{1,14}$/
const REGEX_URL = /^\w+:\/\/\w+([.\-\w+])*(:[0-9]+)?\/?(\/[.\w\-_#?=,|&!]*)*$/

/**
 * @param {boolean} valid
 * @param {string} message
 * @returns {import('../interfaces').Validity}
 */
function validatorMessage (valid, message = '') {
  return {
    valid,
    message: valid ? '' : message
  }
}

const TYPES = {
  required: value => {
    const valid =
      value !== undefined && value !== null && String(value).trim().length

    return validatorMessage(valid, 'Field is mandatory')
  },
  email: value => {
    const valid = !!value.match(REGEX_EMAIL)

    return validatorMessage(valid, 'Please enter a valid email address')
  },
  url: (value, ...args) => {
    const url = String(value).trim()
    const valid = !!url.match(REGEX_URL)

    return validatorMessage(valid, `Please enter a valid URL, "${url}" fails`)
  },
  between: (value, min, max) => {
    const length = String(value).trim().length
    const valid = length >= min && length <= max

    return validatorMessage(
      valid,
      `Please enter between ${min} and ${max} characters`
    )
  },
  password: (value, minChar, passIndex) => {
    const options = {}
    const valid = Password.check(value)

    if (minChar !== undefined) options.minChar = Number(minChar)
    if (passIndex !== undefined) options.passIndex = Number(passIndex)

    return validatorMessage(
      valid.pass,
      valid.pass
        ? ''
        : `Password strength, length or complexity not met: ${valid.verdict}`
    )
  },
  phone: value => {
    const valid = !!String(value).match(REGEX_NUMBER)

    return validatorMessage(valid, 'Please supply a valid phone number')
  },
  oneOf: (value, formList) => {
    const list = Array.isArray(formList) ? formList : formList.split(',')

    return validatorMessage(
      list.indexOf(value) !== -1,
      `Please enter one of: ${list.join(', ')}`
    )
  },
  pattern: (value, pattern, flags = '') => {
    const r = pattern instanceof RegExp ? pattern : new RegExp(pattern, flags)
    const valid = !!String(value).match(r)

    return validatorMessage(
      valid,
      `Please enter a value that matches ${pattern}, ${value} failed`
    )
  },
  optional: (value, ...args) => {
    return value.length
      ? TYPES.between(value, ...args)
      : validatorMessage(true, null)
  },
  number: value => {
    const isNumber = value != null && value !== '' && !isNaN(Number(value))

    return validatorMessage(isNumber, 'Please provide a numeric value')
  },
  date: (value, format = 'DD-MMM-YYYY', strict = false) => {
    const newFormat = strict ? format : undefined
    const isValid =
      value != null && value !== ''
        ? moment(value, newFormat, strict).isValid()
        : false

    return validatorMessage(isValid, 'Please provide a valid date')
  },
  checked: value => {
    return validatorMessage(!!value, 'You need to check this box')
  }
}

function Validators () {}

Validators.rules = TYPES

/**
 * Define a new validator with the name `rule`
 * @param {string} rule
 * @param {import('../interfaces').Validator} callback
 */
Validators.defineRule = function (rule, callback) {
  TYPES[rule] = callback
}

/**
 * Get an existing validator or an empty one if it does not exist
 * @param {string} rule
 * @returns {import('../interfaces').Validator}
 */
Validators.getRule = function (rule) {
  return TYPES[rule]
    ? TYPES[rule]
    : () => {
      // no validator found. Just pass it
      return {
        valid: true
      }
    }
}

/**
 * Validate a value with a rule.
 * @param {string} rule
 * @param {*} value
 * @param {...any} args
 */
Validators.validate = function (rule, value, ...args) {
  return this.getRule(rule)(value, ...args)
}

export default Validators
